import type { FilterMode } from '@indutrust/shared-model';
import type { EntityId } from '@ngrx/signals/entities';

export type FilterUrlState = {
  filterId: string;
  mode: FilterMode;
  selectedValues: EntityId[];
};

/**
 * Parses a comma-separated list of filter ids from a URL query value.
 */
export function parseCommaSeparatedIds(
  value: string | null | undefined
): EntityId[] {
  if (!value?.trim()) {
    return [];
  }

  return value.split(',').map((part) => {
    const trimmed = part.trim();
    const num = Number(trimmed);
    return Number.isFinite(num) && String(num) === trimmed ? num : trimmed;
  });
}

/**
 * Serializes filter selected values to a comma-separated URL query value.
 * Returns `null` when there is nothing to persist.
 */
export function serializeCommaSeparatedIds(values: EntityId[]): string | null {
  if (values.length === 0) {
    return null;
  }

  return values.map(String).join(',');
}

/**
 * Reads human-readable filter query params into a typed restore payload.
 *
 * @param params - Flat query param map (`filterId` → value).
 * @param filterIds - Allowed filter keys (unknown keys are ignored).
 */
export function parseFilterUrlParams(
  params: Record<string, string | undefined>,
  filterIds: readonly string[]
): FilterUrlState[] {
  return filterIds.flatMap((filterId) => {
    const selectedValues = parseCommaSeparatedIds(params[filterId]);
    const mode: FilterMode =
      params[`${filterId}_mode`] === 'advanced' ? 'advanced' : 'simple';

    if (selectedValues.length === 0) {
      return [];
    }

    return [{ filterId, mode, selectedValues }];
  });
}

/**
 * Builds a flat query-param map from current filter selections.
 * Keys with no selection are set to `null` so callers can remove stale params.
 */
export function buildFilterUrlParams(
  filters: {
    id: EntityId;
    mode: FilterMode;
    selectedValues: EntityId[];
  }[],
  filterIds: readonly string[]
): Record<string, string | null> {
  const result: Record<string, string | null> = {};
  const filterMap = new Map(
    filters.map((filter) => [String(filter.id), filter])
  );

  for (const filterId of filterIds) {
    const filter = filterMap.get(filterId);
    result[filterId] = filter
      ? serializeCommaSeparatedIds(filter.selectedValues)
      : null;
    result[`${filterId}_mode`] =
      filter?.mode === 'advanced' ? 'advanced' : null;
  }

  return result;
}

/**
 * Returns a copy of `params` with all known filter keys and their `_mode`
 * suffixes removed. Unrelated query keys are preserved.
 */
export function stripFilterKeysFromParams(
  params: Record<string, string | undefined>,
  filterIds: readonly string[]
): Record<string, string | undefined> {
  const keysToRemove = new Set(
    filterIds.flatMap((filterId) => [filterId, `${filterId}_mode`])
  );

  return Object.fromEntries(
    Object.entries(params).filter(([key]) => !keysToRemove.has(key))
  );
}

/**
 * Returns `true` when the params object contains at least one known filter key.
 */
export function hasFilterUrlParams(
  params: Record<string, string | undefined>,
  filterIds: readonly string[]
): boolean {
  return filterIds.some(
    (filterId) =>
      params[filterId] !== undefined || params[`${filterId}_mode`] !== undefined
  );
}
